/**
 * Upload batches and recent KPI definition / plan / actual changes.
 */

import React from 'react';
import { Card, CardContent } from '@/components/ui/Card';
import type { LoadHistoryChange, LoadHistoryUpload } from './types';

interface KpiLoadHistoryProps {
  uploads: LoadHistoryUpload[];
  changes: LoadHistoryChange[];
  loading: boolean;
  t: (key: string) => string;
}

function formatWhen(value: string | null | undefined): string {
  if (!value) return '—';
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? String(value) : parsed.toLocaleString();
}

export const KpiLoadHistory: React.FC<KpiLoadHistoryProps> = ({ uploads, changes, loading, t }) => {
  const kindLabel = (kind: string) => {
    if (kind === 'target' || kind === 'plan') return t('kpi.loadKindPlan') || 'Plan';
    if (kind === 'actual') return t('kpi.loadKindActuals') || 'Actuals';
    return t('kpi.loadChangeDefinition') || 'KPI';
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
      <Card variant="outline">
        <CardContent className="p-3">
          <h2 className="text-sm font-semibold text-[var(--color-surface-text)] mb-2">
            {t('kpi.loadUploadHistory')}
          </h2>
          {loading ? (
            <p className="text-xs text-[var(--color-secondary-text)]">{t('common.loading')}</p>
          ) : uploads.length === 0 ? (
            <p className="text-xs text-[var(--color-secondary-text)]">{t('kpi.loadUploadHistoryEmpty')}</p>
          ) : (
            <div className="overflow-x-auto max-h-72">
              <table className="w-full text-[11px]">
                <thead className="sticky top-0 bg-[var(--color-secondary-bg)]">
                  <tr>
                    <th className="px-2 py-1 text-left">{t('kpi.loadHistoryWhen')}</th>
                    <th className="px-2 py-1 text-left">{t('kpi.loadHistoryWho')}</th>
                    <th className="px-2 py-1 text-left">{t('kpi.loadHistoryKind')}</th>
                    <th className="px-2 py-1 text-left">{t('kpi.loadHistoryFile')}</th>
                    <th className="px-2 py-1 text-right">{t('kpi.loadHistoryRows')}</th>
                  </tr>
                </thead>
                <tbody>
                  {uploads.map((row) => (
                    <tr key={row.id} className="border-t border-[var(--color-border)]">
                      <td className="px-2 py-1 whitespace-nowrap">{formatWhen(row.createdAt)}</td>
                      <td className="px-2 py-1">{row.actorName || '—'}</td>
                      <td className="px-2 py-1">{kindLabel(row.kind)}</td>
                      <td className="px-2 py-1">{row.fileName || '—'}</td>
                      <td className="px-2 py-1 text-right">{row.rowCount}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
      <Card variant="outline">
        <CardContent className="p-3">
          <h2 className="text-sm font-semibold text-[var(--color-surface-text)] mb-2">
            {t('kpi.loadChangeHistory')}
          </h2>
          {loading ? (
            <p className="text-xs text-[var(--color-secondary-text)]">{t('common.loading')}</p>
          ) : changes.length === 0 ? (
            <p className="text-xs text-[var(--color-secondary-text)]">{t('kpi.loadChangeHistoryEmpty')}</p>
          ) : (
            <div className="overflow-x-auto max-h-72">
              <table className="w-full text-[11px]">
                <thead className="sticky top-0 bg-[var(--color-secondary-bg)]">
                  <tr>
                    <th className="px-2 py-1 text-left">{t('kpi.loadHistoryWhen')}</th>
                    <th className="px-2 py-1 text-left">{t('kpi.loadHistoryWho')}</th>
                    <th className="px-2 py-1 text-left">{t('kpi.kpiName')}</th>
                    <th className="px-2 py-1 text-left">{t('kpi.loadHistoryKind')}</th>
                    <th className="px-2 py-1 text-left">{t('kpi.loadHistoryDetail')}</th>
                  </tr>
                </thead>
                <tbody>
                  {changes.map((row, index) => (
                    <tr key={`${row.kind}-${row.kpiName}-${row.changedAt}-${index}`} className="border-t border-[var(--color-border)]">
                      <td className="px-2 py-1 whitespace-nowrap">{formatWhen(row.changedAt)}</td>
                      <td className="px-2 py-1">{row.actorName || '—'}</td>
                      <td className="px-2 py-1">{row.kpiName}</td>
                      <td className="px-2 py-1">{kindLabel(row.kind)}</td>
                      <td className="px-2 py-1">
                        {[row.detail, row.periodTo, row.value == null ? null : String(row.value)]
                          .filter(Boolean)
                          .join(' · ') || '—'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};
